import {Text} from '@dropbox/dig-components/typography';
import {CelebrateLine, DropboxLine, OutOfOfficeLine} from '@dropbox/dig-icons/assets';
import {format} from 'date-fns';
import {t} from 'i18next';
import {useMemo} from 'react';
import {TileScroller} from 'views/home/dashboard/TileScroller';

import {CalendarPersonRow} from './CalendarPersonRow';
import {
  AnniversaryPerson,
  anniversaryYearsLabel,
  BirthdayPerson,
  PtoPerson,
  recurringDateInRange,
  relationLabel,
  spanLabel,
} from './teamCalendar';

type CalendarEntry =
  | {kind: 'pto'; sortDate: Date; person: PtoPerson}
  | {kind: 'birthday'; sortDate: Date; person: BirthdayPerson}
  | {kind: 'anniversary'; sortDate: Date; person: AnniversaryPerson};

// Type glyph mirrors the calendar tooltip (PTO / birthday / anniversary).
const entryIcon = (kind: CalendarEntry['kind']) => {
  switch (kind) {
    case 'pto':
      return OutOfOfficeLine;
    case 'birthday':
      return CelebrateLine;
    case 'anniversary':
      return DropboxLine;
  }
};

// Trailing label, matching the tooltip format. The icon (passed separately) sits
// just before this text in the row.
const entryLabel = (entry: CalendarEntry): string => {
  switch (entry.kind) {
    case 'pto':
      return spanLabel(entry.person);
    case 'birthday':
      return format(entry.sortDate, 'MMM d');
    case 'anniversary':
      return `${format(entry.sortDate, 'MMM d')} • ${anniversaryYearsLabel(entry.person.years)}`;
  }
};

/**
 * Flat "what's happening" list for the viewed range — PTO spans and birthdays
 * interleaved by date. Mirrors the calendar's data so the two modes stay in
 * sync. Unlike other dashboard lists this isn't truncated to a "View all"
 * footer: the full range scrolls inside the tile (it's a browsable calendar
 * view, not a glanceable summary).
 */
export const CalendarList = ({
  people,
  birthdays,
  anniversaries,
  rangeStart,
  rangeEnd,
}: {
  people: PtoPerson[];
  birthdays: BirthdayPerson[];
  anniversaries: AnniversaryPerson[];
  /** Days shown (the month, or six weeks ahead); places recurring events in the right year. */
  rangeStart: Date;
  rangeEnd: Date;
}) => {
  const entries = useMemo(() => {
    // Callers pass only events inside the range, so the fallback never applies.
    const occurrence = (person: {month: number; day: number}) =>
      recurringDateInRange(person, rangeStart, rangeEnd) ??
      new Date(rangeStart.getFullYear(), person.month - 1, person.day);
    const combined: CalendarEntry[] = [
      ...people.map((person): CalendarEntry => ({kind: 'pto', sortDate: person.start, person})),
      ...birthdays.map(
        (person): CalendarEntry => ({
          kind: 'birthday',
          sortDate: occurrence(person),
          person,
        })
      ),
      ...anniversaries.map(
        (person): CalendarEntry => ({
          kind: 'anniversary',
          sortDate: occurrence(person),
          person,
        })
      ),
    ];
    return combined.sort((left, right) => {
      if (left.sortDate.getTime() !== right.sortDate.getTime()) {
        return left.sortDate.getTime() - right.sortDate.getTime();
      }
      return left.person.name.localeCompare(right.person.name);
    });
  }, [people, birthdays, anniversaries, rangeStart, rangeEnd]);

  if (entries.length === 0) {
    return (
      <Text size="small" color="faint">
        {t('home_empty_people_title')}
      </Text>
    );
  }

  return (
    <TileScroller>
      {entries.map((entry) => (
        <CalendarPersonRow
          key={`${entry.kind}-${entry.person.id}`}
          name={entry.person.name}
          ldap={entry.person.ldap}
          photoUrl={entry.person.photoUrl}
          relation={relationLabel(entry.person.reason)}
          icon={entryIcon(entry.kind)}
          label={entryLabel(entry)}
        />
      ))}
    </TileScroller>
  );
};
